/**
 * Life4Earth V2 — Security API Adapter
 *
 * The single source of truth for security_users, community_memberships,
 * community_role_assignments, project_acl, collection_memberships,
 * collection_project_security_links, and user_collection_preferences.
 *
 * DEV-932: replaced the legacy src/Data/Security/* flatfile reads. Those
 * files no longer exist; this module is the only path.
 *
 * Endpoint: /api/Security
 */

import { apiFetch, getCurrentUserGuid } from "./client";
import { normalizeGuid } from "../utils/guid";
import { invalidateOnboardingState } from "./onboarding";
// DEV-969. Mutations in this file must invalidate the `security` cache
// key so user / role / membership / ACL changes propagate immediately
// to every subscriber. Same pattern as communityMetadata.ts.
import { invalidateAndRefetch, QUERY_KEYS } from "./queryCache";

/**
 * apiFetch wrapper for mutations that affect the `security` cache key.
 * Mirrors apiMutateCommunity in communityMetadata.ts.
 */
async function apiMutateSecurity<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const result = await apiFetch<T>(path, options);
  invalidateAndRefetch(QUERY_KEYS.security);
  return result;
}

// ── Response Types ──────────────────────────────────────────────

export interface SecurityUserResponse {
  id: string;
  userGuid: string;
  name: string;
  displayName: string;
  email: string;
  notes: string;
  primaryLocationEntityGuid: string | null;
  profileImageUrl: string;
  heroImageUrl: string;
  /** Avatar picker re-editable config (JSON string); null for uploaded photos. */
  avatarConfig?: string | null;
  screenName: string | null;
  countryCode: string | null;
  dateOfBirth: string | null;
  themePreference: string;
  /** DEV-1448 — server-backed Default Community View pref
      (null = unset, 'overview' = browse page, else a community guid). */
  defaultCommunityView?: string | null;
  operationalCommunityGuid: string | null;
  showHomeCommunity: boolean;
  showOperationalCommunity: boolean;
  isSystemAdmin: boolean;
  isActive: boolean;
  isSearchable: boolean;
  /** Feed Member Discovery decision 5 — who may send this user a contact
   * request: anyone | members_of_my_communities | contacts_of_contacts | no_one. */
  contactability?: string;
  /** Feed Member Discovery decision 4 — default-IN opt-out for being surfaced
   * in fellow org-members' discovery cards. */
  discoveryAmplification?: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  /** Registration timestamp — the "Added" date for day-zero obligations. */
  createdAt?: string | null;
  // ── DEV-906: Account validation state for milestone evaluator ──
  isTwoFactorEnabled?: boolean;
  eulaAcceptedAt?: string | null;
  eulaVersionAccepted?: number | null;
  tosAcceptedAt?: string | null;
  /** Voice-ladder gate (2026-07-07): community-standards acknowledgment. */
  communityStandardsAcceptedAt?: string | null;
  communityStandardsVersionAccepted?: number | null;
  /** entity_follows count — feeds the binary ring predicate (ringStatus). */
  followCount?: number;
}

export interface UpdateUserProfileRequest {
  name?: string;
  displayName?: string;
  email?: string;
  notes?: string;
  primaryLocationEntityGuid?: string | null;
  profileImageUrl?: string;
  heroImageUrl?: string;
  avatarConfig?: string | null;
  screenName?: string;
  countryCode?: string;
  dateOfBirth?: string | null;
  operationalCommunityGuid?: string | null;
  showHomeCommunity?: boolean;
  showOperationalCommunity?: boolean;
  isSearchable?: boolean;
  /** Feed Member Discovery decision 5. */
  contactability?: string;
  /** Feed Member Discovery decision 4. */
  discoveryAmplification?: boolean;
}

export interface CommunityMembershipResponse {
  id: string;
  userGuid: string;
  communityEntityGuid: string;
  membershipType: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface CommunityRoleAssignmentResponse {
  id: string;
  userGuid: string;
  communityEntityGuid: string;
  role: string;
  assignedByUserGuid: string | null;
  assignedAt: string | null;
}

export interface ProjectAclResponse {
  id: string;
  projectGuid: string;
  userGuid: string;
  role: string;
  isActive: boolean;
  grantedByUserGuid: string | null;
  grantedAt: string | null;
}

export interface CollectionMembershipSecResponse {
  id: string;
  collectionGuid: string;
  userGuid: string;
  role: string;
}

export interface CollectionProjectSecurityLinkResponse {
  id: string;
  collectionGuid: string;
  /**
   * DEV-1096. Polymorphic discriminator — "project" (default) or
   * "mini_project". Pre-DEV-1096 callers see this field absent; treat
   * missing as "project" for backward compat.
   */
  memberType?: "project" | "mini_project";
  /** Nullable post-DEV-1096 — null for mini-project security links. */
  projectGuid: string | null;
  /** DEV-1096. Set for mini-project rows. */
  miniProjectGuid?: string | null;
  override: string;
}

export interface UserCollectionPreferenceResponse {
  id: string;
  userGuid: string;
  collectionGuid: string;
  defaultGroupByFieldKey: string | null;
  defaultSortFieldKey: string | null;
  defaultSortDir: string | null;
}

export interface CreateProjectAclRequest {
  projectGuid: string;
  userGuid: string;
  role: string;
  grantedByUserGuid?: string;
}

// ── Shape Converters ────────────────────────────────────────────

export function securityUserToSeedShape(u: SecurityUserResponse): Record<string, any> {
  return {
    user_guid: normalizeGuid(u.userGuid),
    name: u.name,
    display_name: u.displayName,
    email: u.email,
    notes: u.notes,
    primary_location_entity_guid: normalizeGuid(u.primaryLocationEntityGuid),
    profile_image_url: u.profileImageUrl ?? undefined,
    hero_image_url: u.heroImageUrl ?? undefined,
    screen_name: u.screenName ?? undefined,
    country_code: u.countryCode ?? undefined,
    theme_preference: u.themePreference ?? 'dark',
    // DEV-1448 — server value; localStorage is only an optimistic cache.
    default_community_view: u.defaultCommunityView ?? null,
    operational_community_guid: normalizeGuid(u.operationalCommunityGuid),
    show_home_community: u.showHomeCommunity,
    show_operational_community: u.showOperationalCommunity,
    is_system_admin: u.isSystemAdmin,
    is_active: u.isActive,
    is_searchable: u.isSearchable,
    last_login_at: u.lastLoginAt ?? undefined,
    last_login_ip: u.lastLoginIp ?? undefined,
    // Registration date — the Getting-started checklist's "Added" column
    // (the obligations date from signup, chunk A).
    created_at: u.createdAt ?? null,
    // DEV-906: account validation state. Carried through to the synthetic
    // milestone evaluator so the Tasks tab and Validation panel can detect
    // 2FA + EULA completion. Snake-case for parity with other seed fields.
    is_two_factor_enabled: u.isTwoFactorEnabled ?? false,
    eula_accepted_at: u.eulaAcceptedAt ?? null,
    eula_version_accepted: u.eulaVersionAccepted ?? null,
    tos_accepted_at: u.tosAcceptedAt ?? null,
    // Voice-ladder gate — the Getting-started standards act computes
    // currency from these (mirrors the EULA fields above).
    community_standards_accepted_at: u.communityStandardsAcceptedAt ?? null,
    community_standards_version_accepted: u.communityStandardsVersionAccepted ?? null,
    // Binary ring predicate input (Skip 2026-07-08, ringStatus.ts).
    follow_count: u.followCount ?? 0,
  };
}

export function communityMembershipToSeedShape(m: CommunityMembershipResponse): Record<string, any> {
  return {
    // DEV-1372: preserve the row id — DELETE /communitymemberships/{id}
    // consumers (CommunityAudienceTab remove) need it; the seed shape
    // previously dropped it, making the lookup silently undefined.
    id: m.id,
    user_guid: normalizeGuid(m.userGuid),
    community_entity_guid: normalizeGuid(m.communityEntityGuid),
    membership_type: m.membershipType,
    status: m.status,
    started_at: m.startedAt ?? undefined,
    ended_at: m.endedAt ?? undefined,
  };
}

export function communityRoleToSeedShape(r: CommunityRoleAssignmentResponse): Record<string, any> {
  return {
    user_guid: normalizeGuid(r.userGuid),
    community_entity_guid: normalizeGuid(r.communityEntityGuid),
    role: r.role,
    assigned_by_user_guid: normalizeGuid(r.assignedByUserGuid),
    assigned_at: r.assignedAt ?? undefined,
  };
}

export function projectAclToSeedShape(a: ProjectAclResponse): Record<string, any> {
  return {
    id: a.id, // Preserve API record ID so DELETE calls can target the correct row
    project_guid: normalizeGuid(a.projectGuid),
    user_guid: normalizeGuid(a.userGuid),
    role: a.role,
    is_active: a.isActive !== false, // Preserve activation state; default true
    granted_by_user_guid: normalizeGuid(a.grantedByUserGuid),
    granted_at: a.grantedAt ?? undefined,
  };
}

export function collectionMembershipSecToSeedShape(m: CollectionMembershipSecResponse): Record<string, any> {
  return {
    collection_guid: normalizeGuid(m.collectionGuid),
    user_guid: normalizeGuid(m.userGuid),
    role: m.role,
  };
}

export function securityLinkToSeedShape(l: CollectionProjectSecurityLinkResponse): Record<string, any> {
  // DEV-1096. Polymorphic security link — propagate the discriminator and
  // both nullable polymorphic guids. Legacy consumers reading only
  // project_guid get a null for mini-project rows, which naturally
  // excludes them from project-centric cascades (the safe legacy
  // behavior). New consumers should branch on member_type.
  return {
    collection_guid: normalizeGuid(l.collectionGuid),
    member_type: l.memberType ?? "project",
    project_guid: l.projectGuid ? normalizeGuid(l.projectGuid) : null,
    mini_project_guid: l.miniProjectGuid ? normalizeGuid(l.miniProjectGuid) : null,
    override: l.override,
  };
}

export function preferenceToSeedShape(p: UserCollectionPreferenceResponse): Record<string, any> {
  return {
    user_guid: normalizeGuid(p.userGuid),
    collection_guid: normalizeGuid(p.collectionGuid),
    default_group_by_field_key: p.defaultGroupByFieldKey ?? undefined,
    default_sort_field_key: p.defaultSortFieldKey ?? undefined,
    default_sort_dir: p.defaultSortDir ?? undefined,
  };
}

// ── API Functions ────────────────────────────────────────────────

export async function fetchAllSecurityUsers(): Promise<SecurityUserResponse[]> {
  return apiFetch<SecurityUserResponse[]>("/api/Security/users/all");
}

export async function fetchCurrentUser(): Promise<SecurityUserResponse> {
  return apiFetch<SecurityUserResponse>("/api/Security/users/me");
}

export async function fetchUserByGuid(userGuid: string): Promise<SecurityUserResponse> {
  return apiFetch<SecurityUserResponse>(`/api/Security/users/${userGuid}`);
}

export async function updateUserProfile(
  userGuid: string,
  request: UpdateUserProfileRequest
): Promise<SecurityUserResponse> {
  const res = await apiMutateSecurity<SecurityUserResponse>(`/api/Security/users/${userGuid}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
  // Profile completeness (photo + bio) is a rung basic — a self-profile
  // save can complete the Explorer→Contributor promotion; notify every
  // onboarding hook instance so the celebration popup fires live
  // (Skip bug report, 2026-07-08). Admin edits of OTHER users skip this.
  if (userGuid.toUpperCase() === getCurrentUserGuid().toUpperCase()) {
    invalidateOnboardingState();
  }
  return res;
}

export async function fetchAllCommunityMemberships(): Promise<CommunityMembershipResponse[]> {
  return apiFetch<CommunityMembershipResponse[]>("/api/Security/communitymemberships/all");
}

export async function fetchAllCommunityRoles(): Promise<CommunityRoleAssignmentResponse[]> {
  return apiFetch<CommunityRoleAssignmentResponse[]>("/api/Security/communityroles/all");
}

export async function fetchAllProjectAcl(): Promise<ProjectAclResponse[]> {
  return apiFetch<ProjectAclResponse[]>("/api/Security/projectacl/all");
}

export async function fetchProjectAclByProject(projectGuid: string): Promise<ProjectAclResponse[]> {
  return apiFetch<ProjectAclResponse[]>(`/api/Security/projectacl?projectGuid=${projectGuid}`);
}

export async function fetchAllCollectionMembershipsSec(): Promise<CollectionMembershipSecResponse[]> {
  return apiFetch<CollectionMembershipSecResponse[]>("/api/Security/collectionmemberships/all");
}

// ── Collection Membership CRUD ─────────────────────────────────

export interface UpsertCollectionMembershipRequest {
  collectionGuid: string;
  userGuid: string;
  role: string; // member_viewer | member_contributor | collection_admin
}

export async function upsertCollectionMembership(
  request: UpsertCollectionMembershipRequest
): Promise<CollectionMembershipSecResponse> {
  return apiMutateSecurity<CollectionMembershipSecResponse>("/api/Security/collectionmemberships", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function deleteCollectionMembership(id: string): Promise<void> {
  return apiMutateSecurity<void>(`/api/Security/collectionmemberships/${id}`, { method: "DELETE" });
}

export async function fetchAllSecurityLinks(): Promise<CollectionProjectSecurityLinkResponse[]> {
  return apiFetch<CollectionProjectSecurityLinkResponse[]>("/api/Security/securitylinks/all");
}

export async function fetchAllUserPreferences(): Promise<UserCollectionPreferenceResponse[]> {
  return apiFetch<UserCollectionPreferenceResponse[]>("/api/Security/preferences/all");
}

// DEV-932 — bulk-replace endpoint for project ACL.
export interface ReplaceProjectAclEntry {
  userGuid: string;
  /** "viewer" | "contributor" | "kpi_manager" | "schedule_editor" | "owner_admin" */
  role: string;
  /** Per-entry override of the request-level grantedByUserGuid. */
  grantedByUserGuid?: string | null;
}

export interface ReplaceProjectAclRequest {
  entries: ReplaceProjectAclEntry[];
  /** Default granted_by for new/reactivated rows when an entry doesn't carry its own. */
  grantedByUserGuid?: string | null;
}

/**
 * Atomically replace a project's active ACL with the desired set. Server
 * diffs current vs desired and applies in a single transaction. Returns
 * the resulting active ACL.
 */
export async function replaceProjectAcl(
  projectGuid: string,
  request: ReplaceProjectAclRequest,
): Promise<ProjectAclResponse[]> {
  return apiMutateSecurity<ProjectAclResponse[]>(
    `/api/Security/projects/${projectGuid}/acl/replace`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

export async function createProjectAclEntry(request: CreateProjectAclRequest): Promise<ProjectAclResponse> {
  return apiMutateSecurity<ProjectAclResponse>("/api/Security/projectacl", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function deactivateProjectAclEntry(id: string): Promise<void> {
  return apiMutateSecurity<void>(`/api/Security/projectacl/${id}`, { method: "DELETE" });
}

// ── Community Membership CRUD ──────────────────────────────────

export interface UpsertCommunityMembershipRequest {
  userGuid: string;
  communityEntityGuid: string;
  membershipType?: string; // home | joined | former_resident | works_in | studies_in | org_member
  status: string; // active | inactive | invited | pending | suspended | banned
}

export async function upsertCommunityMembership(
  request: UpsertCommunityMembershipRequest
): Promise<CommunityMembershipResponse> {
  return apiMutateSecurity<CommunityMembershipResponse>("/api/Security/communitymemberships", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function deactivateCommunityMembership(id: string): Promise<void> {
  return apiMutateSecurity<void>(`/api/Security/communitymemberships/${id}`, { method: "DELETE" });
}

// ── Community Role Assignment CRUD ────────────────────────────

export interface UpsertCommunityRoleRequest {
  userGuid: string;
  communityEntityGuid: string;
  role: string; // reader | content_contributor | content_creator | admin | owner
  assignedByUserGuid?: string;
}

export async function upsertCommunityRole(
  request: UpsertCommunityRoleRequest
): Promise<CommunityRoleAssignmentResponse> {
  return apiMutateSecurity<CommunityRoleAssignmentResponse>("/api/Security/communityroles", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function deleteCommunityRole(id: string): Promise<void> {
  return apiMutateSecurity<void>(`/api/Security/communityroles/${id}`, { method: "DELETE" });
}

// ── Screen Name Availability ────────────────────────────────────

export async function checkScreenNameAvailable(
  screenName: string,
  excludeUserGuid?: string
): Promise<boolean> {
  try {
    const users = await fetchAllSecurityUsers();
    const normalized = screenName.trim().toLowerCase();
    return !users.some(
      (u) =>
        u.screenName?.toLowerCase() === normalized &&
        (!excludeUserGuid || u.userGuid.toUpperCase() !== excludeUserGuid.toUpperCase())
    );
  } catch {
    return true; // allow if check fails
  }
}

// ── Collection Project Security Links (CRUD) ─────────────────

export interface UpsertSecurityLinkRequest {
  collectionGuid: string;
  /**
   * DEV-1096. Polymorphic discriminator. Defaults to "project" server-side
   * when omitted, matching the pre-DEV-1096 payload shape.
   */
  memberType?: "project" | "mini_project";
  projectGuid?: string | null;
  miniProjectGuid?: string | null;
  override: string; // inherit | exclude | view | edit
}

export async function upsertSecurityLink(
  request: UpsertSecurityLinkRequest
): Promise<CollectionProjectSecurityLinkResponse> {
  return apiMutateSecurity<CollectionProjectSecurityLinkResponse>("/api/Security/securitylinks", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function deleteSecurityLink(id: string): Promise<void> {
  return apiMutateSecurity<void>(`/api/Security/securitylinks/${id}`, { method: "DELETE" });
}
